import type { NavigateFunction } from 'react-router-dom'
import type { AuthSessionPayload } from '@/lib/auth/hydrateFromAuthSession'
import {
  beginSessionTransition,
  endSessionTransition,
  finalizeAuthenticatedSession,
} from '@/lib/auth/finalizeAuthenticatedSession'
import type { AuthRedirectPayload } from '@/lib/auth/postAuthRedirect'
import { impersonateUser as impersonateUserApi, stopImpersonate as stopImpersonateApi } from '@/apis/superAdmin'
import { useAuthStore } from '@/store/useAuthStore'

export interface ImpersonationResult {
  role: string
}

function resolveImpersonationRole(response: {
  role?: string
  session?: { role?: string; user?: { role?: string } }
}): string {
  return (
    response.role ||
    response.session?.role ||
    response.session?.user?.role ||
    'freelancer'
  )
}

async function resetStartImpersonationOnFailure(): Promise<void> {
  useAuthStore.setState({ impersonating: false, sessionSilentSwap: false })
  const { setSuppressToasts } = await import('@/utils/messageClient')
  setSuppressToasts(false)
}

function buildImpersonationSession(
  session: AuthSessionPayload,
  impersonating: boolean
): AuthSessionPayload {
  return {
    ...session,
    impersonating,
  }
}

const impersonationTransitionBase = {
  mode: 'scoped' as const,
  suppressToasts: true,
  silent: true,
}

const SUPER_ADMIN_REDIRECT: AuthRedirectPayload = { path: '/super-admin' }

function resolveImpersonationRedirect(session: AuthSessionPayload): AuthRedirectPayload {
  if (session.onboarding.required_path) {
    return { path: session.onboarding.required_path, state: { isEdit: false } }
  }
  return { path: session.navigation.default_path || '/missions' }
}

/** Démarre l'impersonation : navigate d'abord → hydrate (évite flash super-admin). */
export async function startImpersonationFlow(
  userId: string,
  navigate: NavigateFunction
): Promise<ImpersonationResult> {
  await beginSessionTransition({
    ...impersonationTransitionBase,
    preserveSuperAdminData: true,
  })

  try {
    const response = await impersonateUserApi(userId)
    if (response?.code !== 200 || !response.data?.session) {
      throw new Error(response?.message || 'IMPERSONATION_FAILED')
    }

    const role = resolveImpersonationRole(response.data)
    const session = buildImpersonationSession(response.data.session, true)

    await finalizeAuthenticatedSession({
      navigate,
      session,
      role,
      impersonating: true,
      preHydrateRedirect: resolveImpersonationRedirect(session),
      transition: 'none',
      endTransition: false,
      suppressToasts: true,
    })

    return { role }
  } catch (error) {
    await resetStartImpersonationOnFailure()
    throw error
  } finally {
    endSessionTransition({ suppressToasts: true })
  }
}

/** Quitte l'impersonation : navigate super-admin d'abord → hydrate admin (sans flash page team). */
export async function stopImpersonationFlow(navigate: NavigateFunction): Promise<void> {
  await beginSessionTransition({
    ...impersonationTransitionBase,
    preserveSuperAdminData: true,
    deferCacheClear: true,
  })

  try {
    const response = await stopImpersonateApi()
    if (response?.code !== 200) {
      throw new Error(response?.message || 'ADMIN_SESSION_RESTORE_FAILED')
    }

    // Pass session directly from response (avoids forceRefetch round-trip).
    // Using forceRefetch here creates a race: admin cookies are set but globalRole is
    // still 'team-admin' until GET /auth/session returns — team-scoped queries fire
    // with admin cookies during that window and get 403.
    // Passing session inline makes applyAuthSessionToClient run synchronously right
    // after navigation, before React Query processes cache-removal notifications.
    await finalizeAuthenticatedSession({
      navigate,
      session: response?.data?.session ?? null,
      impersonating: false,
      preHydrateRedirect: SUPER_ADMIN_REDIRECT,
      clearSessionCacheAfterRedirect: true,
      preserveSuperAdminCache: true,
      transition: 'none',
      endTransition: false,
      suppressToasts: true,
    })
  } finally {
    endSessionTransition({ suppressToasts: true })
  }
}

export function clearImpersonationStorage(): void {
  useAuthStore.setState({
    impersonating: false,
    sessionTransitioning: false,
    sessionSilentSwap: false,
  })
}

export function isImpersonating(): boolean {
  return useAuthStore.getState().impersonating
}
